// src/Component/Sidebar.jsx — matches backend permissions.py
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Star,
  Clock,
  Layout,
  Share2,
  File,
  PieChart,
  Folder,
  Bell,
  ChevronDown,
  Plus,
  Headset,
  LogOut,
  StickyNote,
  User,
  Megaphone,
  Smartphone,
  Calendar,
  ClipboardList,
  UserCheck,
  CalendarCheck,
  KeyRound,
  ScanBarcode,
} from 'lucide-react';
import { logout } from '../features/auth/authSlice';

// ─── Role rank constants — MUST stay in sync with backend permissions.py ──────
const ROLE_RANKS = {
  'ADMIN': 10,
  'OPS_HEAD_ADMIN': 9,
  'HR_HEAD': 9,
  'SALES_HEAD': 9,
  'TECH_ADMIN': 9,
  'CHIEF_DEVELOPMENT_HEAD': 9,
  'FL_ADMIN': 9,
  'MANAGER': 7,
  'HR_MANAGER': 7,
  'TECH_MANAGER': 7,
  'DEVELOPMENT_MANAGER': 7,
  'FLOOR_SUPERVISOR': 7,
  'TEAM_MANAGER': 5,
  'HR_COORDINATOR': 5,
  'FLOOR_COORDINATOR': 5,
  'SALES_EXECUTIVE': 3,
  'TECH_EXECUTIVE': 3,
  'FRONTEND_BACKEND_EXECUTIVE': 3,
  'PAYROLL_ASSISTANT': 3,
  'TECH_ASSISTANT': 3,
  'FRONTEND_BACKEND_ASSISTANT': 3,
  'OFFICE_ASSISTANT': 3,
  'SALES_ASSISTANT': 2,
  'SPA': 2,
  'PA': 1,
};

// HR roles — Level-2 leave approvers
const HR_ROLES = ['HR_HEAD', 'HR_MANAGER', 'HR_COORDINATOR'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getUserRank = (user) =>
  user?.employee_profile ? (ROLE_RANKS[user.employee_profile.role] ?? 0) : 0;

const getUserRole = (user) => user?.employee_profile?.role ?? null;

const isAdmin          = (user) => getUserRole(user) === 'ADMIN';
const isHRRole         = (user) => { const r = getUserRole(user); return r ? HR_ROLES.includes(r) : false; };
const isDeptHeadOrAbove     = (user) => getUserRank(user) >= 9;
const isManagerOrAbove      = (user) => getUserRank(user) >= 7;
const isTeamManagerOrAbove  = (user) => getUserRank(user) >= 5;

/**
 * Can manage/approve leaves?
 *
 * Backend rule: rank ≥ 5, same department, strictly higher than employee.
 * The sidebar only checks rank ≥ 5 (the dept/rank comparison happens per-leave
 * on the backend). HR is already rank ≥ 5 so it's covered automatically.
 */
const canManageLeaves = (user) => isTeamManagerOrAbove(user);

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  sidebar: {
    width: '20%',
    minWidth: '60px',
    height: '100vh',
    transition: 'width 0.3s ease',
    position: 'fixed',
    left: 0,
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
  },
  sidebarCollapsed: { width: '60px' },
  nav: { flex: 1, overflowY: 'auto', padding: '8px 0 40px', scrollbarWidth: 'thin', scrollbarColor: '#ccc transparent' },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    color: '#333',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    position: 'relative',
    textDecoration: 'none',
  },
  menuIcon:  { width: '20px', height: '20px', marginRight: '12px' },
  menuLabel: { fontSize: '14px', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  submenu:   { marginLeft: '28px', paddingLeft: '12px', borderLeft: '1px solid #eee' },
  badge:     { backgroundColor: '#ff4757', color: '#fff', padding: '2px 6px', borderRadius: '10px', fontSize: '12px', marginLeft: '8px' },
  newTag:    { color: '#ff4757', fontSize: '12px', marginLeft: '8px' },
  collapseButton: {
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    borderTop: '1px solid #eee',
    transition: 'background-color 0.2s ease',
  },
  plusIcon: { marginLeft: 'auto', transition: 'transform 0.2s ease' },
};

// ─── MenuItem ─────────────────────────────────────────────────────────────────
const MenuItem = ({ icon: Icon, label, badge, isNew, hasSubmenu, onClick, isOpen, isCollapsed, path }) =>
  path ? (
    <Link to={path} style={{ ...styles.menuItem, textDecoration: 'none', color: '#333' }} onClick={onClick}>
      <Icon style={styles.menuIcon} />
      {!isCollapsed && (
        <>
          <span style={styles.menuLabel}>{label}</span>
          {badge && <span style={styles.badge}>{badge}</span>}
          {isNew && <span style={styles.newTag}>New</span>}
          {hasSubmenu && (
            <Plus style={{ ...styles.plusIcon, transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          )}
        </>
      )}
    </Link>
  ) : (
    <div onClick={onClick} style={styles.menuItem}>
      <Icon style={styles.menuIcon} />
      {!isCollapsed && (
        <>
          <span style={styles.menuLabel}>{label}</span>
          {badge && <span style={styles.badge}>{badge}</span>}
          {isNew && <span style={styles.newTag}>New</span>}
          {hasSubmenu && (
            <Plus style={{ ...styles.plusIcon, transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          )}
        </>
      )}
    </div>
  );

// ─── Sidebar ──────────────────────────────────────────────────────────────────
const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openMenus, setOpenMenus] = useState({
    storeInfo: false,
    employeeDetails: false,
    reports: false,
    alerts: false,
    attendance: false,
    leaveManagement: false,
  });

  const dispatch = useDispatch();
  const user     = useSelector((state) => state.auth.user);

  const toggleMenu  = (menuId) => setOpenMenus((prev) => ({ ...prev, [menuId]: !prev[menuId] }));
  const handleLogout = () => dispatch(logout());

  // ── Role flags ───────────────────────────────────────────────────────────
  const userIsAdmin          = isAdmin(user);
  const userIsDeptHeadOrAbove = isDeptHeadOrAbove(user);
  const userIsManagerOrAbove  = isManagerOrAbove(user);
  const userIsTeamMgrOrAbove  = isTeamManagerOrAbove(user);
  const userCanManageLeaves   = canManageLeaves(user);   // rank ≥ 5

  const menuItems = [
    // ── Dashboard ────────────────────────────────────────────────────────
    {
      icon: Layout,
      label: 'Dashboard',
      path: '/',
      visible: true,
    },

    // ── Store Info ───────────────────────────────────────────────────────
    {
      icon: Folder,
      label: 'Store Info',
      id: 'storeInfo',
      hasSubmenu: true,
      visible: true,
      submenu: [
        { icon: File,     label: 'Create Store',          path: '/storemanagementapp', visible: userIsAdmin },
        { icon: PieChart, label: 'Gas Station Dashboard', path: '/stores',             visible: userIsAdmin },
        { icon: File,     label: 'Store Reports',         path: '/storereportstable',  visible: true },
        { icon: StickyNote, label: 'Store Notes',         path: '/store-notes',        visible: true },
        { icon: Megaphone,  label: 'Announcements',       path: '/announcements',      visible: true },
      ],
    },

    // ── Employee Details — Team Manager and above (rank ≥ 5) ─────────────
    {
      icon: Headset,
      label: 'Employee Details',
      id: 'employeeDetails',
      hasSubmenu: true,
      visible: userIsTeamMgrOrAbove,
      submenu: [
        { icon: Star, label: 'Employee Cards', path: '/employeecards', visible: userIsTeamMgrOrAbove },
      ],
    },

    // ── Reports ──────────────────────────────────────────────────────────
    {
      icon: File,
      label: 'Reports',
      id: 'reports',
      hasSubmenu: true,
      visible: true,
      submenu: [
        { icon: File,   label: 'Create Report Form', path: '/reports/new',       visible: true },
        { icon: Folder, label: 'Client Queries',     path: '/clientqueriestable', visible: true },
      ],
    },

    // ── Cashier Assistance ──────────────────────────────────────────────
    {
      icon: Bell,
      label: 'Cashier Assistance',
      id: 'cashierAssistance',
      hasSubmenu: false,
      visible: true,
      path: '/cashier-assistance',
    },

    // ── Alerts ───────────────────────────────────────────────────────────
    {
      icon: Bell,
      label: 'Alerts',
      id: 'alerts',
      hasSubmenu: true,
      visible: true,
      submenu: [
        { icon: Clock, label: 'Suspicious Transaction Alerts', path: '/suspicioustransactionalerts', visible: true },
      ],
    },

    // ── Attendance — mixed visibility ─────────────────────────────────────
    // CHANGED:
    //   Attendance Dashboard: was Admin only → now Team Manager and above (rank ≥ 5)
    //   Employee Summary:     was Admin only → now Team Manager and above (rank ≥ 5)
    //   Backend scopes returned records to the viewer's dept + rank automatically.
    {
      icon: Calendar,
      label: 'Attendance',
      id: 'attendance',
      hasSubmenu: true,
      visible: true,
      submenu: [
        {
          icon: UserCheck,
          label: 'Attendance Dashboard',
          path: '/admin/attendance',
          visible: userIsTeamMgrOrAbove,  // ← was userIsAdmin
        },
        {
          icon: PieChart,
          label: 'Employee Summary',
          path: '/admin/summary',
          visible: userIsTeamMgrOrAbove,  // ← was userIsAdmin
        },
        {
          icon: Calendar,
          label: 'My Attendance',
          path: '/employee/attendance',
          visible: true,
        },
      ],
    },

    // ── Leave Management — mixed visibility ───────────────────────────────
    // CHANGED:
    //   Manage Leaves: was (HR roles + Managers + Dept Heads + Admin)
    //                  now Team Manager and above (rank ≥ 5)
    //   Backend enforces same-dept + outrank per individual leave.
    {
      icon: ClipboardList,
      label: 'Leave Management',
      id: 'leaveManagement',
      hasSubmenu: true,
      visible: true,
      submenu: [
        {
          icon: Plus,
          label: 'Apply Leave',
          path: '/employee/leave/apply',
          visible: true,
        },
        {
          icon: CalendarCheck,
          label: 'My Leave Status',
          path: '/employee/leave/status',
          visible: true,
        },
        {
          icon: ClipboardList,
          label: 'Manage Leaves',
          path: '/admin/leaves',
          visible: userCanManageLeaves,   // ← rank ≥ 5
        },
      ],
    },

    // ── Device Management — Admin only ────────────────────────────────────
    {
      icon: Smartphone,
      label: 'Device Management',
      path: '/device-management',
      visible: userIsAdmin,
    },

    // ── Software licences — Admin only ────────────────────────────────────
    {
      icon: KeyRound,
      label: 'Software Licences',
      path: '/licenses',
      visible: userIsAdmin,
    },

    // ── POS Bridge PCs — Admin only ───────────────────────────────────────
    {
      icon: ScanBarcode,
      label: 'POS Bridge PCs',
      path: '/pos-bridge-pcs',
      visible: userIsAdmin,
    },

    // ── Profile ───────────────────────────────────────────────────────────
    {
      icon: User,
      label: 'Profile',
      path: user?.employee_profile?.id
        ? `/employees/${user.employee_profile.id}`
        : '/employees',
      visible: true,
    },

    // ── Logout ────────────────────────────────────────────────────────────
    {
      icon: LogOut,
      label: 'Logout',
      onClick: handleLogout,
      visible: true,
    },
  ];

  return (
    <div style={{ ...styles.sidebar, ...(isCollapsed && styles.sidebarCollapsed) }}>
      <style>{`
        .sidebar-nav::-webkit-scrollbar { width: 4px; }
        .sidebar-nav::-webkit-scrollbar-track { background: transparent; }
        .sidebar-nav::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 999px; }
        .sidebar-nav::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
      `}</style>
      <nav className="sidebar-nav" style={styles.nav}>
        {menuItems
          .filter((item) => item.visible !== false)
          .map((item, index) => (
            <div key={index}>
              <MenuItem
                icon={item.icon}
                label={item.label}
                badge={item.badge}
                isNew={item.isNew}
                hasSubmenu={item.hasSubmenu}
                onClick={() => {
                  if (item.hasSubmenu) toggleMenu(item.id);
                  else if (item.onClick) item.onClick();
                }}
                isOpen={openMenus[item.id]}
                isCollapsed={isCollapsed}
                path={item.path}
              />
              {!isCollapsed && item.hasSubmenu && openMenus[item.id] && (
                <div style={styles.submenu}>
                  {item.submenu
                    .filter((sub) => sub.visible !== false)
                    .map((sub, si) => (
                      <div key={si}>
                        <MenuItem
                          icon={sub.icon}
                          label={sub.label}
                          badge={sub.badge}
                          isNew={sub.isNew}
                          hasSubmenu={sub.hasSubmenu}
                          onClick={() => sub.hasSubmenu && toggleMenu(sub.id)}
                          isOpen={openMenus[sub.id]}
                          isCollapsed={isCollapsed}
                          path={sub.path}
                        />
                        {!isCollapsed && sub.hasSubmenu && openMenus[sub.id] && (
                          <div style={styles.submenu}>
                            {sub.submenu.map((nested, ni) => (
                              <MenuItem
                                key={ni}
                                icon={nested.icon}
                                label={nested.label}
                                badge={nested.badge}
                                isNew={nested.isNew}
                                hasSubmenu={nested.hasSubmenu}
                                onClick={nested.onClick}
                                isOpen={openMenus[nested.id]}
                                isCollapsed={isCollapsed}
                                path={nested.path}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          ))}
      </nav>

      <div style={styles.collapseButton} onClick={() => setIsCollapsed(!isCollapsed)}>
        <ChevronDown
          style={{
            transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
          }}
        />
      </div>
    </div>
  );
};

export default Sidebar;